import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { ControlsOf } from '../../utilities/form.types';
import { Translation } from '../translation';
import { ApprovalEntityType, ApproverType } from './approval-policy';

/** One approver row of the policy form. */
export interface ApproverFormModel {
  type: ApproverType;
  /** Username when type is User, role name when Role. */
  value: string;
  /** Null = parallel. */
  order: number | null;
  isRequired: boolean;
  /** Null = always applies. */
  fromAmountUsd: number | null;
}

export interface ApprovalPolicyFormModel {
  name: Translation;
  isActive: boolean;
  minimumApprovals: number;
  appliesTo: ApprovalEntityType[];
  defaultEntities: ApprovalEntityType[];
  approvers: ApproverFormModel[];
}

export type ApproverFormGroup = FormGroup<ControlsOf<ApproverFormModel>>;
export type PolicyNameFormGroup = FormGroup<ControlsOf<Translation>>;

/** The policy form: one control per field, the name as an en/es group and the approver rows as a FormArray. */
export type ApprovalPolicyFormControls = ControlsOf<Omit<ApprovalPolicyFormModel, 'name' | 'approvers'>> & {
  name: PolicyNameFormGroup;
  approvers: FormArray<ApproverFormGroup>;
};

/** Group-level twin of the en/es required validators, so the save report reads "Name is required". */
export function bothLanguagesRequired(group: AbstractControl): ValidationErrors | null {
  const value = group.value as Partial<Translation> | null;
  return value?.en?.trim() && value?.es?.trim() ? null : { required: true };
}

export const approvalPolicyFieldLabels: Record<keyof ApprovalPolicyFormModel, string> = {
  name: 'ApprovalPolicy_Details_Form_Field_Name',
  isActive: 'ApprovalPolicy_Details_Form_Field_IsActive',
  minimumApprovals: 'ApprovalPolicy_Details_Form_Field_MinimumApprovals',
  appliesTo: 'ApprovalPolicy_Details_Form_Field_AppliesTo',
  defaultEntities: 'ApprovalPolicy_Details_Form_Field_DefaultEntities',
  approvers: 'ApprovalPolicy_Details_Form_Field_Approvers',
};

export const approverFieldLabels: Record<keyof ApproverFormModel, string> = {
  type: 'ApprovalPolicy_Details_Form_Field_ApproverType',
  value: 'ApprovalPolicy_Details_Form_Field_ApproverValue',
  order: 'ApprovalPolicy_Details_Form_Field_ApproverOrder',
  isRequired: 'ApprovalPolicy_Details_Form_Field_ApproverIsRequired',
  fromAmountUsd: 'ApprovalPolicy_Details_Form_Field_ApproverFromAmountUsd',
};

/** Search filters of the list page. Status travels as a number because the dynamic filter carries no booleans. */
export interface ApprovalPolicySearchFormModel {
  name: string | null;
  isActive: number | null;
  appliesTo: number[] | null;
  defaultFor: number[] | null;
}
